/**
 * 图片透明度检测工具
 * @description 提供安全、高性能的图片透明度检测和智能格式选择功能
 * @author Assistant
 */

// 图片格式相关常量
export const IMAGE_FORMAT_CONFIG = {
    PNG: {
        mimeType: 'image/png' as const,
        extension: 'png' as const,
        quality: 1,
        supportsTransparency: true,
    },
    JPEG: {
        mimeType: 'image/jpeg' as const,
        extension: 'jpg' as const,
        quality: 0.9,
        supportsTransparency: false,
    },
} as const;

export type ImageFormat = (typeof IMAGE_FORMAT_CONFIG)[keyof typeof IMAGE_FORMAT_CONFIG];

/**
 * 检查 ImageData 是否包含透明像素
 * @param imageData ImageData 对象
 * @returns boolean 是否包含透明度
 */
export const checkImageDataTransparency = (imageData: ImageData): boolean => {
    const { data } = imageData;

    // 每4个元素为一个像素：R, G, B, A
    // Alpha 通道在第4个位置（索引3, 7, 11, ...）
    for (let i = 3; i < data.length; i += 4) {
        if (data[i] < 255) {
            return true; // 发现透明或半透明像素
        }
    }
    return false;
};

/**
 * 错误报告函数类型定义
 */
interface ErrorReportOptions {
    error: Error | unknown;
    componentName: string;
    additionalInfo?: Record<string, unknown>;
}

/**
 * 默认错误报告函数（可以通过参数覆盖）
 */
const defaultErrorReporter = (options: ErrorReportOptions): void => {
    console.error(`[${options.componentName}] Error:`, options.error);
    if (options.additionalInfo) {
        console.error('Additional info:', options.additionalInfo);
    }
};

const MAX_FULL_SCAN_PIXELS = 1000000;
const MIN_SAMPLE_RATE = 0.01;
const MAX_SAMPLE_RATE = 1;
const DEFAULT_SAMPLE_RATE = 0.1;
const MIN_SAMPLE_BLOCK_SIZE = 8;
const MAX_SAMPLE_BLOCK_SIZE = 128;
const EDGE_SAMPLE_LIMIT = 12;
const GRID_SAMPLE_LIMIT = 6;
const DIAGONAL_SAMPLE_LIMIT = 16;
const SELECT_FORMAT_SAMPLE_RATE = 0.15;

const clampNumber = (value: number, min: number, max: number): number => {
    if (min > max) {
        return value;
    }
    if (value < min) {
        return min;
    }
    if (value > max) {
        return max;
    }
    return value;
};

const normalizeSampleRate = (rate?: number): number => {
    if (typeof rate !== 'number' || !isFinite(rate)) {
        return DEFAULT_SAMPLE_RATE;
    }
    return clampNumber(rate, MIN_SAMPLE_RATE, MAX_SAMPLE_RATE);
};

const pickCoordinate = (index: number, count: number, dimension: number, blockSize: number): number => {
    if (count <= 1) {
        return Math.max(0, Math.floor((dimension - blockSize) / 2));
    }
    const usable = Math.max(0, dimension - blockSize);
    if (usable === 0) {
        return 0;
    }
    const ratio = clampNumber(index, 0, count - 1) / (count - 1);
    return Math.min(usable, Math.floor(usable * ratio));
};

const sampleRegionHasTransparency = (
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    regionWidth: number,
    regionHeight: number,
): boolean => {
    const canvasWidth = ctx.canvas.width;
    const canvasHeight = ctx.canvas.height;
    const width = Math.max(1, Math.min(Math.floor(regionWidth), canvasWidth));
    const height = Math.max(1, Math.min(Math.floor(regionHeight), canvasHeight));
    const maxX = Math.max(0, canvasWidth - width);
    const maxY = Math.max(0, canvasHeight - height);
    const safeX = clampNumber(Math.floor(x), 0, maxX);
    const safeY = clampNumber(Math.floor(y), 0, maxY);
    const imageData = ctx.getImageData(safeX, safeY, width, height);
    return checkImageDataTransparency(imageData);
};

const sampleEdgesForTransparency = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    blockWidth: number,
    blockHeight: number,
): boolean => {
    const horizontalSamples = clampNumber(Math.ceil(width / blockWidth), 3, EDGE_SAMPLE_LIMIT);
    const verticalSamples = clampNumber(Math.ceil(height / blockHeight), 3, EDGE_SAMPLE_LIMIT);

    for (let index = 0; index < horizontalSamples; index += 1) {
        const x = pickCoordinate(index, horizontalSamples, width, blockWidth);
        if (sampleRegionHasTransparency(ctx, x, 0, blockWidth, blockHeight)) {
            return true;
        }
        if (height > blockHeight) {
            const bottomY = height - blockHeight;
            if (sampleRegionHasTransparency(ctx, x, bottomY, blockWidth, blockHeight)) {
                return true;
            }
        }
    }

    for (let index = 0; index < verticalSamples; index += 1) {
        const y = pickCoordinate(index, verticalSamples, height, blockHeight);
        if (sampleRegionHasTransparency(ctx, 0, y, blockWidth, blockHeight)) {
            return true;
        }
        if (width > blockWidth) {
            const rightX = width - blockWidth;
            if (sampleRegionHasTransparency(ctx, rightX, y, blockWidth, blockHeight)) {
                return true;
            }
        }
    }

    return false;
};

const sampleGridForTransparency = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    blockWidth: number,
    blockHeight: number,
): boolean => {
    const columns = clampNumber(Math.ceil(width / blockWidth), 3, GRID_SAMPLE_LIMIT);
    const rows = clampNumber(Math.ceil(height / blockHeight), 3, GRID_SAMPLE_LIMIT);

    for (let row = 0; row < rows; row += 1) {
        const y = pickCoordinate(row, rows, height, blockHeight);
        for (let col = 0; col < columns; col += 1) {
            const x = pickCoordinate(col, columns, width, blockWidth);
            if (sampleRegionHasTransparency(ctx, x, y, blockWidth, blockHeight)) {
                return true;
            }
        }
    }

    return false;
};

const sampleDiagonalsForTransparency = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    step: number,
): boolean => {
    const limit = Math.min(width, height);
    const maxX = Math.max(0, width - 1);
    const maxY = Math.max(0, height - 1);
    const safeStep = Math.max(1, step);

    for (let offset = 0; offset <= limit; offset += safeStep) {
        const normalized = maxX > 0 ? Math.min(1, offset / maxX) : 0;
        const x = clampNumber(Math.floor(offset), 0, maxX);
        const y = clampNumber(Math.floor(normalized * maxY), 0, maxY);
        if (sampleRegionHasTransparency(ctx, x, y, 1, 1)) {
            return true;
        }

        const mirroredY = clampNumber(maxY - y, 0, maxY);
        if (mirroredY !== y && sampleRegionHasTransparency(ctx, x, mirroredY, 1, 1)) {
            return true;
        }
    }

    return false;
};

const detectTransparencyWithSampling = (
    ctx: CanvasRenderingContext2D,
    width: number,
    height: number,
    sampleRate: number,
): boolean => {
    if (width <= 0 || height <= 0) {
        return false;
    }
    const totalPixels = width * height;
    if (totalPixels <= MAX_FULL_SCAN_PIXELS) {
        const fullImageData = ctx.getImageData(0, 0, width, height);
        return checkImageDataTransparency(fullImageData);
    }

    const normalizedRate = normalizeSampleRate(sampleRate);
    const targetSamplePixels = Math.max(1024, Math.floor(totalPixels * normalizedRate));
    const baseBlockSize = Math.max(1, Math.floor(Math.sqrt(targetSamplePixels)));
    const blockWidth = clampNumber(baseBlockSize, MIN_SAMPLE_BLOCK_SIZE, Math.min(MAX_SAMPLE_BLOCK_SIZE, width));
    const blockHeight = clampNumber(baseBlockSize, MIN_SAMPLE_BLOCK_SIZE, Math.min(MAX_SAMPLE_BLOCK_SIZE, height));

    if (sampleEdgesForTransparency(ctx, width, height, blockWidth, blockHeight)) {
        return true;
    }

    if (sampleGridForTransparency(ctx, width, height, blockWidth, blockHeight)) {
        return true;
    }

    const minDimension = Math.min(width, height);
    const diagonalSamples = clampNumber(Math.round(1 / normalizedRate), 6, DIAGONAL_SAMPLE_LIMIT);
    const diagonalStep = clampNumber(Math.floor(minDimension / diagonalSamples), 1, minDimension);

    return sampleDiagonalsForTransparency(ctx, width, height, diagonalStep);
};

/**
 * 安全检测图片是否包含透明度
 * @param canvas HTML Canvas 元素
 * @param sampleRate 采样率，0-1之间，用于优化大图片检测性能，默认0.1
 * @param reportError 错误报告函数，可选
 * @returns Promise<boolean> 是否包含透明度
 */
export const hasTransparencyAsync = async (
    canvas: HTMLCanvasElement | null,
    sampleRate: number = DEFAULT_SAMPLE_RATE,
    reportError: (options: ErrorReportOptions) => void = defaultErrorReporter,
): Promise<boolean> => {
    if (!canvas) {
        reportError({
            error: new Error('Canvas is null'),
            componentName: 'TransparencyDetector',
            additionalInfo: { method: 'hasTransparencyAsync' },
        });
        return false;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        reportError({
            error: new Error('Cannot get 2d context from canvas'),
            componentName: 'TransparencyDetector',
            additionalInfo: { canvasSize: `${canvas.width}x${canvas.height}` },
        });
        return false;
    }

    const normalizedRate = normalizeSampleRate(sampleRate);

    try {
        return detectTransparencyWithSampling(ctx, canvas.width, canvas.height, normalizedRate);
    } catch (error) {
        reportError({
            error,
            componentName: 'TransparencyDetector',
            additionalInfo: {
                method: 'hasTransparencyAsync',
                canvasSize: `${canvas.width}x${canvas.height}`,
                sampleRate: normalizedRate,
            },
        });
        return true;
    }
};

/**
 * 智能选择图片格式
 * @param canvas Canvas 元素
 * @param originalFormat 原始图片格式（可选）
 * @param reportError 错误报告函数，可选
 * @returns Promise<ImageFormat> 推荐的图片格式配置
 */
export const selectOptimalFormat = async (
    canvas: HTMLCanvasElement | null,
    originalFormat?: string,
    reportError: (options: ErrorReportOptions) => void = defaultErrorReporter,
): Promise<ImageFormat> => {
    if (!canvas) {
        console.warn('Canvas is null, defaulting to PNG format');
        return IMAGE_FORMAT_CONFIG.PNG;
    }

    const ctx = canvas.getContext('2d');
    if (!ctx) {
        reportError({
            error: new Error('Cannot get 2d context from canvas'),
            componentName: 'TransparencyDetector',
            additionalInfo: { canvasSize: `${canvas.width}x${canvas.height}` },
        });
        return IMAGE_FORMAT_CONFIG.PNG;
    }

    try {
        const hasAlpha = detectTransparencyWithSampling(ctx, canvas.width, canvas.height, SELECT_FORMAT_SAMPLE_RATE);
        if (hasAlpha) {
            return IMAGE_FORMAT_CONFIG.PNG;
        }
        return IMAGE_FORMAT_CONFIG.JPEG;
    } catch (error) {
        reportError({
            error,
            componentName: 'TransparencyDetector',
            additionalInfo: {
                method: 'selectOptimalFormat',
                originalFormat,
                canvasSize: `${canvas.width}x${canvas.height}`,
            },
        });
        return IMAGE_FORMAT_CONFIG.PNG;
    }
};

/**
 * 便捷函数：检测图片透明度并返回推荐格式
 * @param canvas Canvas 元素
 * @param options 配置选项
 * @returns Promise<{hasTransparency: boolean, recommendedFormat: ImageFormat}>
 */
export interface TransparencyDetectionOptions {
    sampleRate?: number;
    originalFormat?: string;
    reportError?: (options: ErrorReportOptions) => void;
}

export const detectTransparencyAndFormat = async (
    canvas: HTMLCanvasElement | null,
    options: TransparencyDetectionOptions = {},
): Promise<{ hasTransparency: boolean; recommendedFormat: ImageFormat }> => {
    const { sampleRate = DEFAULT_SAMPLE_RATE, originalFormat, reportError = defaultErrorReporter } = options;

    const hasTransparency = await hasTransparencyAsync(canvas, sampleRate, reportError);
    const recommendedFormat = await selectOptimalFormat(canvas, originalFormat, reportError);

    return {
        hasTransparency,
        recommendedFormat,
    };
};
